import React, { useState, useContext, useEffect } from "react";
import { Input, Row, Col, Container, CustomInput, Label } from "reactstrap";
import Symbol from "../../Fields/Symbol";
import { onChangeHeader, onChange, onChangeItem } from "../../IO";
import Files from "../../Fields/Files/Files";
import { LocalINTL, SimpleSuggest } from "../../Fields";
import { CurrencyAsset, IOMode, IOType, IOTransferType } from "../../../../IO/types";
import { Combobox, Numeric, Field } from "../../../Order/Field/Field";
import { Dispatch } from "../../store/store";
import Account from "../../Fields/Account";
import { commissionSuffix } from "../../../Order/Form/commons";
import { isEmpty } from "../Default";

export const Subscription = ({ account }) => {
  const { dispatch, services, state } = useContext(Dispatch);
  const { io, fields, entities, permissions } = state;
  const { errors, readonly } = fields;
  const { allowValues, allowCash } = permissions;
  const header = io.itemsHeader;
  const simpleItem = io.items[0];
  const [balance, setBalance] = useState({
    loading: false,
    data: [],
  });
  const [decimalAmount, setDecimalAmount] = useState();

  useEffect(() => {
    if (account && simpleItem.Id) {
      onChange(dispatch, { isSubscriptionOrRedemption: true, IOType: IOType.Deposit });
      onChangeHeader(dispatch, {
        AccountNumber: account.AccountNumber,
        AccountName: account.AccountName,
        AccountId: account.AccountId,
        AssetId: null,
        AssetTicker: "",
        CurrencyISO: "USD - DOLAR AMERICANO",
        CurrencyId: "1",
        decimal: 2,
        IOType: IOType.Deposit,
      });
      onChangeItem(dispatch, simpleItem.Id, { AssetId: null, AssetTicker: "", CurrencyISO: "USD - DOLAR AMERICANO", CurrencyId: "1", decimal: 2 });
    }
  }, [account]);

  // XOR operator
  const readOnlyTypeSelector = allowValues ? !allowCash : allowCash;
  const accountEmpty = isEmpty(header.AccountId);
  const currencyEmpty = isEmpty(simpleItem.CurrencyId);

  useEffect(() => {
    if (header.IsCurrency && !header.BankAccountIsThirdParty) {
      if (!isEmpty(header.AccountId) && !isEmpty(simpleItem.CurrencyId)) {
        services
          .fetchGetAccountBanks({
            accountId: account.AccountId,
            currencyId: simpleItem.CurrencyId,
            fundIncomesOnly: true,
          })
          .then((r) => r.response)
          .then((r) => {
            dispatch({
              type: "UPDATE_ACCOUNT_BANKS",
              accountBanks: r.accountBanks,
            });
            if (header.BankAccountItemId === null && r.accountBanks) {
              const item = r.accountBanks[0];
              onChangeHeader(dispatch, {
                ...item,
              });
            } else {
              const item = r.accountBanks.find((a) => a.BankAccountItemId === header.BankAccountItemId);
              onChangeHeader(dispatch, {
                ...item,
              });
            }
          });
      } else {
        dispatch({ type: "UPDATE_ACCOUNT_BANKS", accountBanks: [] });
        onChangeHeader(dispatch, {
          BankAccountItemId: null,
          BankAccountName: null,
        });
      }
    }
  }, [header.AccountId, simpleItem.CurrencyId]);

  useEffect(() => {
    if (header.AccountId) {
      setBalance({ ...balance, loading: true });
      services.fetchAccountBuyingPower({ accountId: header.AccountId }).then((response) => {
        const { buyingpower } = response.response;
        setBalance({ ...balance, data: buyingpower });
      });
    } else {
      setBalance({ ...balance, data: [] });
    }
    setBalance({ ...balance, loading: false });
  }, [header.AccountId, header.IsCurrency]);

  useEffect(() => {
    if (header.AccountId && !header.IsCurrency && simpleItem.AssetId) {
      setBalance({ ...balance, loading: true });
      services.fetchAccountSellingPower({ accountId: header.AccountId, assetId: simpleItem.AssetId }).then((response) => {
        const { sellingpower } = response.response;
        setBalance({ ...balance, data: sellingpower });
      });
    } else {
      setBalance({ ...balance, data: [] });
    }
    setBalance({ ...balance, loading: false });
    services.fetchGetAssetInformation({ assetId: simpleItem.AssetId }).then((response) => {
      setDecimalAmount(response.info.SubAssetClassDecimalPlaces);
    });
  }, [simpleItem.AssetId]);

  return (
    <Container>
      <Row>
        {/* Left */}
        <Col xs={6} lg={6}>
          <Row className="mb-2" style={{ marginTop: "22px" }}>
            <Col lg={6} xs={12}>
              <LocalINTL isLocal={io.Local} readonly={readonly.Local} onChange={(v) => onChange(dispatch, v)} />
            </Col>
            <Col lg={6} xs={6}>
              <Combobox
                darken
                disabled
                onChange={(v) =>
                  onChangeHeader(dispatch, {
                    IsCurrency: v == CurrencyAsset.Cash,
                  })
                }
                value={header.IsCurrency ? CurrencyAsset.Cash : CurrencyAsset.Asset}
                items={Object.values(CurrencyAsset).map((key, index) => ({
                  id: key,
                  description: key,
                }))}
              />
            </Col>
          </Row>

          {/* ##### Symbol ###### */}
          <Symbol
            ioType={IOType.Deposit}
            accountId={header.AccountId}
            isCurrency={header.IsCurrency}
            onChange={(values) => onChangeItem(dispatch, simpleItem.Id, values)}
            error={header.IsCurrency ? errors.simple_CurrencyISO : errors.simple_AssetTicker}
            disabled
            value={header.IsCurrency ? simpleItem.CurrencyISO : simpleItem.AssetTicker}
            fetchSearchSymbol={services.fetchSearchSymbol}
          />
          {/* ##### Quantity ###### */}
          <Field label={"Quantity"} error={errors.simple_Quantity}>
            <Numeric
              value={simpleItem.Quantity}
              placeholder={"Quantity"}
              onChange={(v) => onChangeItem(dispatch, simpleItem.Id, { Quantity: v })}
              invalid={!!errors.simple_Quantity}
              disabled={readonly.simple_Quantity}
              dataTypeDecimalName="quantity"
            />
          </Field>

          {/* ##### Price ###### */}
          {!header.IsCurrency && (
            <Field label={"Price"} error={errors.header_Price}>
              <Numeric
                value={header.Price}
                placeholder={"Price"}
                dataTypeDecimalName="price"
                onChange={(v) => onChangeHeader(dispatch, { Price: v })}
                invalid={!!errors.header_Price}
                disabled={readonly.header_Price}
              />
            </Field>
          )}

          {/* ##### Date ##### */}
          <Field label={"Date"} error={errors.Date}>
            <Input
              tabIndex="3"
              type="date"
              placeholder={"Date"}
              disabled={readonly.Date}
              value={io.Date}
              invalid={!!errors.Date}
              onChange={(e) => onChange(dispatch, { Date: e.target.value })}
            />
          </Field>

          {/* ##### Settlement Date ##### */}
          <Field label={"Settlement Date"} error={errors.SettlementDate}>
            <Input
              tabIndex="3"
              type="date"
              placeholder={"Settlement Date"}
              disabled={readonly.SettlementDate}
              value={io.SettlementDate}
              invalid={!!errors.SettlementDate}
              onChange={(e) => onChange(dispatch, { SettlementDate: e.target.value })}
            />
          </Field>
        </Col>

        {/* Right */}
        <Col xs={12} lg={6}>
          {/* ##### Account ###### */}
          <Account
            AccountId={account.AccountId}
            AccountName={header.AccountName}
            AccountNumber={header.AccountNumber}
            HolderId={header.HolderId}
            HolderName={header.HolderName}
            onChange={(values) => onChangeHeader(dispatch, values)}
            balance={balance}
            readonly={{
              AccountName: true,
              HolderName: false,
            }}
            errors={{
              AccountName: errors.header_AccountName,
              HolderName: errors.header_HolderName,
            }}
            services={{
              fetchAccountHolders: services.fetchAccountHolders,
              fetchSearchAccount: services.fetchSearchAccount,
            }}
          />

          <Files id={io.Id} ioType={io.IOType} readonly={readonly.simple_Quantity} dispatch={dispatch} files={io.files} downloadFile={services.fetchDownloadIOFile} />

          {/* ##### Custodian ###### */}
          <SimpleSuggest
            label={"Received in"}
            service={services.fetchSearchCustodian}
            error={errors.CustodianName}
            disabled={readonly.CustodianName}
            value={io.CustodianName}
            onChange={({ id, name }) => onChange(dispatch, { CustodianId: id, CustodianName: name })}
          />

          {header.IsCurrency && io.Local && (
            <React.Fragment>
              <Field label={"Type"} error={errors.header_TransferType}>
                <Combobox
                  value={header.TransferType}
                  items={[
                    { id: "Transfer", description: "Transfer" },
                    { id: "Cash", description: "Cash" },
                    { id: "Cheque", description: "Cheque" },
                  ]}
                  disabled={readonly.simple_Quantity}
                  invalid={!!errors.header_TransferType}
                  onChange={(v) => onChangeHeader(dispatch, { TransferType: v })}
                  emptyLabel={"Type"}
                />
              </Field>

              {/* ##### Bank Account ##### */}
              {header.TransferType === IOTransferType.Transfer && (
                <React.Fragment>
                  <div className="mt-2 mb-2">
                    <CustomInput
                      label="Third party bank account"
                      type="checkbox"
                      id={`cbox-thirdParty`}
                      checked={header.BankAccountIsThirdParty || false}
                      disabled={readonly.header_BankAccountIsThirdParty}
                      onChange={(v) =>
                        onChangeHeader(dispatch, {
                          BankAccountIsThirdParty: !header.BankAccountIsThirdParty,
                        })
                      }
                    />
                  </div>
                  {header.BankAccountIsThirdParty ? (
                    <React.Fragment>
                      <SimpleSuggest
                        label={"Third party bank"}
                        service={services.fetchSearchCustodianEntity}
                        error={errors.header_BankAccountName}
                        disabled={readonly.simple_Quantity}
                        value={header.BankAccountName || ""}
                        onChange={({ id, name }) =>
                          onChangeHeader(dispatch, {
                            BankAccountId: id,
                            BankAccountName: name,
                          })
                        }
                      />

                      <Field label={"Third party bank account"} error={errors.header_BankAccountNumber}>
                        <Input
                          type={"text"}
                          placeholder={"Account"}
                          value={header.BankAccountNumber || ""}
                          invalid={errors.header_BankAccountNumber}
                          onChange={(e) =>
                            onChangeHeader(dispatch, {
                              BankAccountNumber: e.target.value,
                            })
                          }
                          disabled={readonly.simple_Quantity}
                        />
                      </Field>

                      <Field label={"Third party person name"} error={errors.header_ThirdPartyPersonName}>
                        <Input
                          type={"text"}
                          placeholder={"Person Name"}
                          value={header.ThirdPartyPersonName || ""}
                          invalid={errors.header_ThirdPartyPersonName}
                          onChange={(e) =>
                            onChangeHeader(dispatch, {
                              ThirdPartyPersonName: e.target.value,
                            })
                          }
                          disabled={readonly.header_Quantity}
                        />
                      </Field>

                      <Field label={"Third party address or identification number"} error={errors.header_ThirdPartyIdentificationNumber}>
                        <Input
                          type={"text"}
                          placeholder={"Third party address or identification number"}
                          value={header.ThirdPartyIdentificationNumber || ""}
                          invalid={errors.header_ThirdPartyIdentificationNumber}
                          onChange={(e) =>
                            onChangeHeader(dispatch, {
                              ThirdPartyIdentificationNumber: e.target.value,
                            })
                          }
                          disabled={readonly.header_Quantity}
                        />
                      </Field>

                      <SimpleSuggest
                        label={"Third party Country"}
                        service={services.fetchSearchCountry}
                        error={errors.header_ThirdPartyCountryName}
                        disabled={readonly.header_Quantity}
                        value={header.ThirdPartyCountryName}
                        onChange={({ id, name }) => {
                          onChangeHeader(dispatch, {
                            ThirdPartyCountryId: id,
                            ThirdPartyCountryName: name,
                          });
                        }}
                      />
                    </React.Fragment>
                  ) : (
                    <div>
                      <Field
                        label={"Bank Account"}
                        error={errors.header_BankAccountName}
                        description={accountEmpty || currencyEmpty ? "First select an account and currency" : null}
                      >
                        <Combobox
                          value={header.BankAccountItemId}
                          items={entities.accountBanks.map((a) => ({
                            id: a.BankAccountItemId,
                            description: `${a.BankAccountName} - ${a.BankAccountNumber}`,
                          }))}
                          disabled={accountEmpty || currencyEmpty || readonly.simple_Quantity}
                          invalid={!!errors.header_BankAccountName}
                          onChange={(id) =>
                            onChangeHeader(
                              dispatch,
                              entities.accountBanks.find((a) => a.BankAccountItemId == id)
                            )
                          }
                          emptyLabel={"Bank Account"}
                        />
                      </Field>
                      {header.BankAccountItemId && (
                        <Field label={"Bank Account Holders"}>
                          <div />
                          {header.BankAccountHolders}
                        </Field>
                      )}
                    </div>
                  )}
                </React.Fragment>
              )}

              {/* ##### Cheque ##### */}
              {header.TransferType === IOTransferType.Cheque && (
                <React.Fragment>
                  {/* ##### Third party bank ###### */}
                  <SimpleSuggest
                    label={"Bank"}
                    service={services.fetchSearchCustodianEntity}
                    error={errors.header_BankAccountName}
                    disabled={readonly.simple_Quantity}
                    value={header.BankAccountName || ""}
                    onChange={({ id, name }) =>
                      onChangeHeader(dispatch, {
                        BankAccountId: id,
                        BankAccountName: name,
                      })
                    }
                  />

                  <Field label={"#Cheque"} error={errors.header_ChequeNumber}>
                    <Input
                      type={"text"}
                      placeholder={"Number"}
                      value={header.ChequeNumber || ""}
                      invalid={errors.header_ChequeNumber}
                      onChange={(e) =>
                        onChangeHeader(dispatch, {
                          ChequeNumber: e.target.value,
                        })
                      }
                      disabled={readonly.simple_Quantity}
                    />
                  </Field>
                </React.Fragment>
              )}
            </React.Fragment>
          )}

          <Row>
            {/* ##### Commission ###### */}
            <Col lg={6}>
              <Field label={"Commission"} error={errors.header_Commission}>
                <Numeric
                  value={header.Commission}
                  placeholder={`Commission`}
                  disabled={readonly.header_Commission}
                  onChange={(v) => onChangeHeader(dispatch, { Commission: v })}
                  invalid={!!errors.header_Commission}
                  {...commissionSuffix(null)}
                  dataTypeDecimalName="currency"
                />
              </Field>
            </Col>

            {/* ##### CommissionType ###### */}
            <Col lg={6}>
              <Field label={"Type"} error={errors.header_CommissionType}>
                <Combobox
                  value={header.CommissionType}
                  items={[
                    { id: "FaceValueCash", description: "$ Cash" },
                    { id: "PrincipalPercentage", description: "% Principal" },
                  ]}
                  disabled={readonly.header_CommissionType}
                  invalid={!!errors.header_CommissionType}
                  onChange={(v) => onChangeHeader(dispatch, { CommissionType: v })}
                  emptyLabel={"Type"}
                  style={{ background: "#edf9ff", fontWeight: 500 }}
                />
              </Field>
            </Col>
          </Row>
        </Col>
      </Row>
      {!io.Local && (
        <React.Fragment>
          <Row>
            <Col lg={6} xs={12}>
              {/* ##### Account ###### */}
              <Field label={"#Account"} error={errors.header_BankAccountNumber}>
                <Input
                  type={"text"}
                  placeholder={"#Account"}
                  value={header.BankAccountNumber || ""}
                  invalid={errors.header_BankAccountNumber}
                  onChange={(e) =>
                    onChangeHeader(dispatch, {
                      BankAccountNumber: e.target.value,
                    })
                  }
                  disabled={readonly.header_Quantity}
                />
              </Field>

              {/* ##### Bank ###### */}
              <Field label={"Bank"} error={errors.header_BankAccountName}>
                <Input
                  type={"text"}
                  placeholder={"Bank"}
                  value={header.BankAccountName || ""}
                  invalid={errors.header_BankAccountName}
                  onChange={(e) =>
                    onChangeHeader(dispatch, {
                      BankAccountName: e.target.value,
                    })
                  }
                  disabled={readonly.header_Quantity}
                />
              </Field>

              {/* ##### Intermediary Bank ###### */}
              <Field label={"Intermediary Bank"} error={errors.header_IntermediaryBank}>
                <Input
                  type={"text"}
                  placeholder={"Intermediary Bank"}
                  value={header.IntermediaryBank || ""}
                  onChange={(e) =>
                    onChangeHeader(dispatch, {
                      IntermediaryBank: e.target.value,
                    })
                  }
                  disabled={readonly.header_Quantity}
                  invalid={!!errors.header_IntermediaryBank}
                />
              </Field>
            </Col>

            <Col lg={6} xs={12}>
              {/* ##### Country ###### */}
              <SimpleSuggest
                label={"Country"}
                service={services.fetchSearchCountry}
                error={errors.header_CountryName}
                disabled={readonly.header_Quantity}
                value={header.CountryName}
                onChange={({ id, name }) => {
                  onChangeHeader(dispatch, {
                    CountryId: id,
                    CountryName: name,
                  });
                }}
              />

              {/* ##### ABA/SWIFT ###### */}
              <Field label={"ABA/SWIFT"} error={errors.header_ABASWIFT}>
                <Input
                  type={"text"}
                  placeholder={"ABA/SWIFT"}
                  value={header.ABASWIFT || ""}
                  onChange={(e) => onChangeHeader(dispatch, { ABASWIFT: e.target.value })}
                  invalid={!!errors.header_ABASWIFT}
                  disabled={readonly.header_Quantity}
                />
              </Field>

              {/* ##### Intermediary ABA/SWIFT ###### */}
              <Field label={"Intermediary ABA/SWIFT"} error={errors.header_IntermediaryABASwift}>
                <Input
                  type={"text"}
                  placeholder={"Intermediary ABA/SWIFT"}
                  value={header.IntermediaryABASwift || ""}
                  onChange={(e) =>
                    onChangeHeader(dispatch, {
                      IntermediaryABASwift: e.target.value,
                    })
                  }
                  invalid={!!errors.header_IntermediaryABASwift}
                  disabled={readonly.header_Quantity}
                />
              </Field>
            </Col>
          </Row>
          <Row>
            <Col lg={6} xs={12}>
              <Field label={"Third party person name"} error={errors.header_ThirdPartyPersonName}>
                <Input
                  type={"text"}
                  placeholder={"Person Name"}
                  value={header.ThirdPartyPersonName || ""}
                  invalid={errors.header_ThirdPartyPersonName}
                  onChange={(e) =>
                    onChangeHeader(dispatch, {
                      ThirdPartyPersonName: e.target.value,
                    })
                  }
                  disabled={readonly.header_Quantity}
                />
              </Field>
            </Col>
            <Col lg={6} xs={12}>
              <Field label={"Third party address or identification number"} error={errors.header_ThirdPartyIdentificationNumber}>
                <Input
                  type={"text"}
                  placeholder={"Third party address or identification number"}
                  value={header.ThirdPartyIdentificationNumber || ""}
                  invalid={errors.header_ThirdPartyIdentificationNumber}
                  onChange={(e) =>
                    onChangeHeader(dispatch, {
                      ThirdPartyIdentificationNumber: e.target.value,
                    })
                  }
                  disabled={readonly.header_Quantity}
                />
              </Field>
            </Col>
          </Row>
        </React.Fragment>
      )}
    </Container>
  );
};

export const SubscriptionValidations = (io, userType) => {
  const header = io.itemsHeader;
  const isBulk = io.IOMode == IOMode.MultipleAssets;
  const isThirdPartyAccount = header.TransferType == IOTransferType.Transfer && header.BankAccountIsThirdParty;

  if (isBulk) {
    return {
      form: {
        CustodianName: { empty: !userType || userType != "Advisor" },
      },
      simple: {},
      subForm: {
        AccountName: { empty: true },
        HolderName: { empty: true },
        BankAccountItemId: {
          empty: io.Local && header.IsCurrency && header.TransferType == IOTransferType.Transfer && !isThirdPartyAccount,
        },
        BankAccountId: {
          empty: io.Local && header.IsCurrency && ((header.TransferType == IOTransferType.Transfer && isThirdPartyAccount) || header.TransferType == IOTransferType.Cheque),
        },
        BankAccountName: {
          empty: io.Local && header.IsCurrency && header.TransferType != IOTransferType.Cash,
        },
        BankAccountNumber: {
          empty: io.Local && header.IsCurrency && header.TransferType == IOTransferType.Transfer,
        },
        ChequeNumber: {
          empty: io.Local && header.IsCurrency && header.TransferType == IOTransferType.Cheque,
        },
        IntermediaryBank: { empty: !io.Local },
        CountryName: { empty: !io.Local },
        ABASWIFT: { empty: !io.Local },
        IntermediaryABASwift: { empty: !io.Local },
      },
    };
  }

  return {
    form: {
      CustodianName: { empty: !userType || userType != "Advisor" },
    },
    simple: {
      Quantity: { empty: true },
      AssetTicker: {
        empty: header.IsCurrency == false,
        autosuggestId: "AssetId",
      },
      CurrencyISO: {
        empty: header.IsCurrency == true,
        autosuggestId: "CurrencyId",
      },
    },
    subForm: {
      AccountName: { empty: true },
      HolderName: { empty: true },
      Price: { empty: header.IsCurrency == false },
      BankAccountItemId: {
        empty: io.Local && header.IsCurrency && header.TransferType == IOTransferType.Transfer && !isThirdPartyAccount,
      },
      BankAccountId: {
        empty: io.Local && header.IsCurrency && ((header.TransferType == IOTransferType.Transfer && isThirdPartyAccount) || header.TransferType == IOTransferType.Cheque),
      },
      BankAccountName: {
        empty: (!io.Local && header.IsCurrency && header.TransferType != IOTransferType.Cash) || (io.Local && header.IsCurrency && header.TransferType == IOTransferType.Transfer),
      },
      BankAccountNumber: {
        empty: header.IsCurrency && header.TransferType == IOTransferType.Transfer,
      },
      ChequeNumber: {
        empty: io.Local && header.IsCurrency && header.TransferType == IOTransferType.Cheque,
      },
      CountryName: { empty: !io.Local },
      ThirdPartyCountryName: {
        empty: io.Local && header.IsCurrency && header.TransferType == IOTransferType.Transfer && isThirdPartyAccount,
      },
    },
  };
};
